/* global VideoEncoder, VideoFrame */
import { FiZap } from "react-icons/fi";
import { useState, useRef, useEffect, useCallback } from "react";
import { Muxer, ArrayBufferTarget } from "mp4-muxer";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import Topbar from "../../components/topbar/Topbar";
import Leftbar from "../../components/LeftBar/lefbar";
import "./tweetToReel.css";

const REEL_W = 1080;
const REEL_H = 1920;
const FPS = 30;
const DURATION_S = 12;
const TOTAL_FRAMES = FPS * DURATION_S;
const PAD = 88;

/* ─────────────────────────────────────────────────
   Canvas helpers
───────────────────────────────────────────────── */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  // Split on real newlines first, then word-wrap each line
  const paragraphs = text.split("\n");
  const result = [];
  for (const para of paragraphs) {
    if (!para.trim()) { result.push(""); continue; }
    const words = para.split(" ");
    let line = "";
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        result.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) result.push(line);
  }
  return result;
}

function adaptTextSize(ctx, text, maxWidth, maxLines) {
  for (const size of [62, 54, 46, 40, 34]) {
    ctx.font = `400 ${size}px -apple-system, 'Segoe UI', Arial, sans-serif`;
    const lines = wrapText(ctx, text, maxWidth);
    if (lines.length <= maxLines) return { size, lines };
  }
  ctx.font = `400 34px -apple-system, 'Segoe UI', Arial, sans-serif`;
  return { size: 34, lines: wrapText(ctx, text, maxWidth) };
}

/* Truncate text with ellipsis to fit maxWidth (ctx font must be set first) */
function truncate(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0, hi = text.length;
  while (lo < hi - 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (ctx.measureText(text.slice(0, mid) + "…").width <= maxWidth) lo = mid;
    else hi = mid;
  }
  return text.slice(0, lo) + "…";
}

/* Wrap display name to at most 2 lines; falls back to truncation */
function wrapName(ctx, name, maxW) {
  if (ctx.measureText(name).width <= maxW) return [name];
  const words = name.split(" ");
  for (let i = words.length - 1; i >= 1; i--) {
    const line1 = words.slice(0, i).join(" ");
    if (ctx.measureText(line1).width <= maxW) {
      return [line1, words.slice(i).join(" ")];
    }
  }
  return [truncate(ctx, name, maxW)];
}

/* Draw a single frame — static (no animation), matches X native post style */
function drawFrame(ctx, data, avatarImg, mediaEl) {
  const { authorName, authorHandle, tweetText, verified } = data;

  // White background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, REEL_W, REEL_H);

  const innerW = REEL_W - PAD * 2;
  let y = 120;

  /* ── Avatar ── */
  const AV = 132;
  const avCx = PAD + AV / 2;
  const avCy = y + AV / 2;

  ctx.save();
  ctx.beginPath();
  ctx.arc(avCx, avCy, AV / 2, 0, Math.PI * 2);
  ctx.clip();
  if (avatarImg) {
    ctx.drawImage(avatarImg, PAD, y, AV, AV);
  } else {
    ctx.fillStyle = "#ff6a00";
    ctx.fill();
    ctx.font = "700 58px Arial";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.fillText((authorName || "?")[0].toUpperCase(), avCx, avCy + 20);
  }
  ctx.restore();

  /* ── Name + handle (next to avatar) ── */
  const nameX = PAD + AV + 32;
  const NAME_FONT = "700 46px -apple-system, 'Segoe UI', Arial, sans-serif";
  const XCOM_FONT = "700 38px -apple-system, 'Segoe UI', Arial, sans-serif";

  // Measure X.com first to know reserved space
  ctx.font = XCOM_FONT;
  const xcomLabel = "X.com";
  const xcomW = ctx.measureText(xcomLabel).width;
  const maxNameLineW = (REEL_W - PAD) - nameX - xcomW - 52;

  // Wrap name to up to 2 lines
  ctx.font = NAME_FONT;
  ctx.fillStyle = "#0f1419";
  ctx.textAlign = "left";
  const nameStr = authorName || "Unknown";
  const nameLines = wrapName(ctx, nameStr, maxNameLineW);
  const NAME_LINE_H = 54;

  nameLines.forEach((line, i) => {
    ctx.fillText(line, nameX, y + 50 + i * NAME_LINE_H);
  });

  // Verified badge — always shown
  {
    const firstW = ctx.measureText(nameLines[0]).width;
    const bx = nameX + firstW + 16;
    const by = y + 38;
    const br = 16;
    ctx.fillStyle = "#1d9bf0";
    ctx.beginPath();
    ctx.arc(bx + br, by, br, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(bx + 6, by);
    ctx.lineTo(bx + 14, by + 8);
    ctx.lineTo(bx + 26, by - 8);
    ctx.stroke();
  }

  // Handle — below last name line
  const handleY = y + 50 + (nameLines.length - 1) * NAME_LINE_H + 52;
  ctx.font = "400 38px -apple-system, 'Segoe UI', Arial, sans-serif";
  ctx.fillStyle = "#536471";
  ctx.fillText(`@${authorHandle || "user"}`, nameX, handleY);

  /* ── X.com label — aligned with first name line ── */
  ctx.textAlign = "right";
  ctx.font = XCOM_FONT;
  ctx.fillStyle = "#000000";
  ctx.fillText(xcomLabel, REEL_W - PAD, y + 50);

  // Advance y past the whole header block
  const headerBottom = Math.max(y + AV, handleY + 12);
  y = headerBottom + 72;

  /* ── Tweet text ── */
  const hasMedia = !!mediaEl;
  const maxTextLines = hasMedia ? 12 : 20;
  const { size, lines } = adaptTextSize(ctx, tweetText, innerW, maxTextLines);
  const lineH = size * 1.58;

  ctx.font = `400 ${size}px -apple-system, 'Segoe UI', Arial, sans-serif`;
  ctx.fillStyle = "#0f1419";
  ctx.textAlign = "left";
  for (const line of lines) {
    if (line === "") {
      y += lineH * 0.5; // half-height gap for paragraph breaks
    } else {
      ctx.fillText(line, PAD, y);
      y += lineH;
    }
  }

  y += 52;

  /* ── Media — full bleed width, aspect-ratio correct height ── */
  if (mediaEl) {
    const isVideo = mediaEl.tagName === "VIDEO";
    const natW = isVideo ? mediaEl.videoWidth : mediaEl.naturalWidth;
    const natH = isVideo ? mediaEl.videoHeight : mediaEl.naturalHeight;

    if (natW > 0 && natH > 0) {
      const aspect = natW / natH;
      // Ideal dimensions at full width
      let drawW = REEL_W;
      let drawH = drawW / aspect;
      // If that's taller than the remaining space, scale down keeping ratio
      const maxH = REEL_H - y - 80;
      if (drawH > maxH) {
        drawH = maxH;
        drawW = drawH * aspect;
      }
      const drawX = (REEL_W - drawW) / 2; // center horizontally if narrowed

      const ready = !isVideo || mediaEl.readyState >= 2;
      ctx.save();
      roundRect(ctx, drawX, y, drawW, drawH, 20);
      ctx.clip();
      if (ready) {
        ctx.drawImage(mediaEl, drawX, y, drawW, drawH);
      } else {
        ctx.fillStyle = "#e7e9ea";
        ctx.fillRect(drawX, y, drawW, drawH);
      }
      ctx.restore();
    }
  }
}

/* ─────────────────────────────────────────────────
   WebCodecs path — text-only, fast (no audio)
───────────────────────────────────────────────── */
async function generateTextReel(data, avatarImg, onProgress) {
  const canvas = new OffscreenCanvas(REEL_W, REEL_H);
  const ctx = canvas.getContext("2d");

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: { codec: "avc", width: REEL_W, height: REEL_H },
    fastStart: "in-memory",
    firstTimestampBehavior: "offset",
  });

  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => { throw e; },
  });
  encoder.configure({
    codec: "avc1.42003d",
    width: REEL_W,
    height: REEL_H,
    bitrate: 4_000_000,
    hardwareAcceleration: "prefer-hardware",
  });

  for (let i = 0; i < TOTAL_FRAMES; i++) {
    drawFrame(ctx, data, avatarImg, null);
    const timestamp = Math.round((i / FPS) * 1_000_000);
    const duration  = Math.round(1_000_000 / FPS);
    const frame = new VideoFrame(canvas, { timestamp, duration });
    encoder.encode(frame, { keyFrame: i % 60 === 0 });
    frame.close();

    if (i % 30 === 0) {
      onProgress((i / TOTAL_FRAMES) * 0.95);
      await new Promise(r => setTimeout(r, 0));
    }
    while (encoder.encodeQueueSize > 10) {
      await new Promise(r => setTimeout(r, 1));
    }
  }

  await encoder.flush();
  encoder.close();
  muxer.finalize();
  onProgress(1);
  return new Blob([target.buffer], { type: "video/mp4" });
}

/* ─────────────────────────────────────────────────
   MediaRecorder path — with media/audio, real-time
───────────────────────────────────────────────── */
async function generateMediaReel(data, avatarImg, mediaEl, onProgress, durationS = DURATION_S) {
  const canvas = document.createElement("canvas");
  canvas.width  = REEL_W;
  canvas.height = REEL_H;
  const ctx = canvas.getContext("2d");

  // Capture canvas video stream
  const canvasStream = canvas.captureStream(FPS);
  const tracks = [...canvasStream.getVideoTracks()];

  // Capture audio from video element if it exists
  let audioCtx = null;
  if (mediaEl && mediaEl.tagName === "VIDEO") {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const src = audioCtx.createMediaElementSource(mediaEl);
      const dest = audioCtx.createMediaStreamDestination();
      src.connect(dest);
      // Don't connect to destination — we don't want speaker output during recording
      const audioTrack = dest.stream.getAudioTracks()[0];
      if (audioTrack) tracks.push(audioTrack);
    } catch (e) {
      console.warn("Audio capture not available:", e.message);
    }
  }

  const mimeType =
    MediaRecorder.isTypeSupported("video/mp4;codecs=avc1,mp4a.40.2")
      ? "video/mp4;codecs=avc1,mp4a.40.2"
      : MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";

  const ext = mimeType.startsWith("video/mp4") ? "mp4" : "webm";

  const recorder = new MediaRecorder(new MediaStream(tracks), {
    mimeType,
    videoBitsPerSecond: 4_000_000,
  });
  const chunks = [];
  recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };

  if (mediaEl && mediaEl.tagName === "VIDEO") {
    mediaEl.currentTime = 0;
    mediaEl.loop = false; // play once, full duration
    mediaEl.play().catch(() => {});
  }

  recorder.start(200);

  const startTime = performance.now();
  await new Promise(resolve => {
    function tick() {
      const elapsed = (performance.now() - startTime) / 1000;
      onProgress(Math.min(elapsed / durationS * 0.95, 0.95));
      drawFrame(ctx, data, avatarImg, mediaEl);
      if (elapsed < durationS) {
        requestAnimationFrame(tick);
      } else {
        resolve();
      }
    }
    requestAnimationFrame(tick);
  });

  recorder.stop();
  if (mediaEl && mediaEl.tagName === "VIDEO") mediaEl.pause();
  if (audioCtx) audioCtx.close();

  onProgress(1);
  return new Promise(resolve => {
    recorder.onstop = () =>
      resolve({ blob: new Blob(chunks, { type: mimeType }), ext });
  });
}

function fmtElapsed(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

/* ─────────────────────────────────────────────────
   Component
───────────────────────────────────────────────── */
export default function TweetToReel() {
  const [url, setUrl]               = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorHandle, setAuthorHandle] = useState("");
  const [authorAvatar, setAuthorAvatar] = useState("");
  const [tweetText, setTweetText]   = useState("");
  const [metrics, setMetrics]       = useState(null);
  const [verified, setVerified]     = useState(false);
  const [mediaList, setMediaList]   = useState([]); // [{url, type, width, height}]

  const [videoDuration, setVideoDuration] = useState(DURATION_S); // seconds

  const [fetching, setFetching]       = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [progress, setProgress]       = useState(0);
  const [elapsed, setElapsed]         = useState(0); // seconds since generate started
  const [serverJobId, setServerJobId] = useState(null);       // non-null = currently polling
  const [completedJobId, setCompletedJobId] = useState(null); // persists after polling ends
  const [serverStatus, setServerStatus] = useState("");        // "queued"|"processing"|"completed"|"failed"
  const [serverProgress, setServerProgress] = useState(0);    // 0–1 real progress from worker
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadExt, setDownloadExt] = useState("mp4");
  const [error, setError]             = useState("");
  const [showManual, setShowManual]   = useState(false);

  const canvasRef    = useRef(null);
  const avatarImgRef = useRef(null);  // Image element for avatar
  const mediaElRef   = useRef(null);  // Image or Video element for tweet media
  const animRef      = useRef(null);

  /* Preview loop */
  const renderPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    drawFrame(
      ctx,
      {
        authorName:   authorName   || "Display Name",
        authorHandle: authorHandle || "handle",
        tweetText:    tweetText    || "Paste a tweet URL above to auto-fill the content here.",
        metrics,
        verified,
      },
      avatarImgRef.current,
      mediaElRef.current
    );
    animRef.current = requestAnimationFrame(renderPreview);
  }, [authorName, authorHandle, tweetText, metrics, verified]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(renderPreview);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [renderPreview]);

  /* Load avatar image */
  useEffect(() => {
    if (!authorAvatar) { avatarImgRef.current = null; return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `/api/tweet-to-reel/proxy-image?url=${encodeURIComponent(authorAvatar)}`;
    img.onload  = () => { avatarImgRef.current = img; };
    img.onerror = () => { avatarImgRef.current = null; };
  }, [authorAvatar]);

  /* Load tweet media (photo or video) */
  useEffect(() => {
    const first = mediaList[0];
    if (!first) { mediaElRef.current = null; return; }

    if (first.type === "photo") {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = `/api/tweet-to-reel/proxy-image?url=${encodeURIComponent(first.url)}`;
      img.onload  = () => { mediaElRef.current = img; };
      img.onerror = () => { mediaElRef.current = null; };
    } else if (first.type === "video") {
      const vid = document.createElement("video");
      vid.crossOrigin = "anonymous";
      vid.muted = true; // muted so it can autoplay for preview
      vid.loop  = true;
      vid.playsInline = true;
      vid.src = `/api/tweet-to-reel/proxy-video?url=${encodeURIComponent(first.url)}`;
      vid.load();
      vid.play().catch(() => {});
      mediaElRef.current = vid;
    }
  }, [mediaList]);

  /* Poll server job status */
  useEffect(() => {
    if (!serverJobId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axiosInstance.get(`/tweet-to-reel/job/${serverJobId}`);
        setServerStatus(data.status);
        if (typeof data.progress === "number") setServerProgress(data.progress);
        if (data.status === "completed") {
          clearInterval(interval);
          setCompletedJobId(serverJobId);
          setServerJobId(null);
          setGenerating(false);
          setDownloadUrl(data.reelUrl);
          setDownloadExt("mp4");
          toast.success("Reel ready!");
        } else if (data.status === "failed") {
          clearInterval(interval);
          setServerJobId(null);
          setGenerating(false);
          toast.error("Generation failed: " + (data.error || "unknown error"));
        }
      } catch {
        // transient error — keep polling
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [serverJobId]);

  /* Elapsed timer while generating */
  useEffect(() => {
    if (!generating) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [generating]);

  /* Fetch tweet data */
  const fetchTweet = async (tweetUrl) => {
    if (!tweetUrl.trim()) return;
    setFetching(true);
    setError("");
    try {
      const { data } = await axiosInstance.get(
        `/tweet-to-reel/fetch?url=${encodeURIComponent(tweetUrl.trim())}`
      );
      setAuthorName(data.author?.name || "");
      setAuthorHandle(data.author?.username || "");
      setAuthorAvatar(data.author?.profile_image_url || "");
      setTweetText(data.tweet?.text || "");
      setMetrics(data.tweet?.public_metrics || null);
      setVerified(data.author?.verified || false);
      const media = data.media || [];
      setMediaList(media);
      // Use actual video duration if available, else default 12s
      const firstVideo = media.find(m => m.type === "video");
      setVideoDuration(firstVideo?.duration ? Math.ceil(firstVideo.duration) : DURATION_S);
      setDownloadUrl(null);
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to fetch tweet.";
      setError(msg);
      setShowManual(true);
    } finally {
      setFetching(false);
    }
  };

  /* Auto-fetch when URL looks complete */
  const handleUrlChange = (val) => {
    setUrl(val);
    setError("");
    if (/(?:twitter|x)\.com\/\w+\/status(?:es)?\/\d+/.test(val)) {
      fetchTweet(val);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    if (/(?:twitter|x)\.com\/\w+\/status(?:es)?\/\d+/.test(pasted)) {
      setUrl(pasted);
      fetchTweet(pasted);
      e.preventDefault();
    }
  };

  /* Generate */
  const handleGenerate = async () => {
    if (!tweetText.trim()) { toast.error("Add tweet text first"); return; }
    setGenerating(true);
    setProgress(0);
    setDownloadUrl(null);
    setServerJobId(null);
    setCompletedJobId(null);
    setServerStatus("");
    setServerProgress(0);

    const hasVideo = mediaList.some(m => m.type === "video");
    const firstVideo = mediaList.find(m => m.type === "video");

    if (hasVideo && firstVideo) {
      /* ── Server-side path (FFmpeg worker) — credit deducted by generate-job ── */
      try {
        const { data } = await axiosInstance.post("/tweet-to-reel/generate-job", {
          authorName,
          authorHandle,
          avatarUrl: authorAvatar,
          tweetText,
          videoUrl: firstVideo.url,
          verified,
          videoDuration,
        });
        setServerJobId(data.jobId);
        setServerStatus("queued");
        // generating stays true — polling useEffect will clear it when done
      } catch (err) {
        const errData = err.response?.data;
        if (errData?.error === "out_of_credits") {
          toast.error("No credits remaining. Buy a credit pack to continue.");
        } else {
          toast.error("Could not start job: " + (errData?.message || err.message));
        }
        setGenerating(false);
      }
      return;
    }

    /* ── Client-side path (text-only or photo) — deduct credit separately ── */
    try {
      await axiosInstance.post("/tweet-to-reel/use-credit");
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.error === "out_of_credits") {
        toast.error("No credits remaining. Buy a credit pack to continue.");
      } else {
        toast.error("Could not use credit: " + (errData?.message || err.message));
      }
      setGenerating(false);
      return;
    }

    const frameData = { authorName, authorHandle, tweetText, metrics, verified };

    try {
      const mediaEl = mediaElRef.current;
      let blobUrl, ext;

      if (mediaEl) {
        // Photo: MediaRecorder path (static image, no audio)
        let recordEl = mediaEl;
        const result = await generateMediaReel(
          frameData, avatarImgRef.current, recordEl, setProgress, videoDuration
        );
        blobUrl = URL.createObjectURL(result.blob);
        ext = result.ext;
      } else {
        // Text-only: WebCodecs (fast)
        const blob = await generateTextReel(frameData, avatarImgRef.current, setProgress);
        blobUrl = URL.createObjectURL(blob);
        ext = "mp4";
      }

      setDownloadUrl(blobUrl);
      setDownloadExt(ext);
      toast.success("Reel ready!");
    } catch (err) {
      console.error("Generation error:", err);
      toast.error("Failed to generate: " + (err.message || "Unknown error"));
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setDownloadUrl(null);
    setUrl("");
    setAuthorName("");
    setAuthorHandle("");
    setAuthorAvatar("");
    setTweetText("");
    setMetrics(null);
    setVerified(false);
    setMediaList([]);
    setVideoDuration(DURATION_S);
    setServerJobId(null);
    setCompletedJobId(null);
    setServerStatus("");
    setServerProgress(0);
    setError("");
    setShowManual(false);
  };

  return (
    <div className="ttr-page">
      <Topbar />
      <div className="ttr-leftbar-wrapper"><Leftbar /></div>

      <div className="ttr-header">
        <h1 className="ttr-title">X Clipper</h1>
        <p className="ttr-subtitle">Turn any X post into a 9:16 reel in seconds</p>
      </div>

      <div className="ttr-body">
        {/* Controls */}
        <div className="ttr-controls">

          {/* URL input */}
          <div>
            <p className="ttr-section-label">Paste tweet URL</p>
            <div className="ttr-url-row">
              <input
                className="ttr-input"
                placeholder="https://x.com/user/status/123..."
                value={url}
                onChange={e => handleUrlChange(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={e => e.key === "Enter" && fetchTweet(url)}
              />
              <button
                className="ttr-fetch-btn"
                onClick={() => fetchTweet(url)}
                disabled={fetching || !url.trim()}
              >
                {fetching ? "..." : "Fetch"}
              </button>
            </div>
            {error && <p className="ttr-error">{error}</p>}
          </div>

          {/* Manual toggle */}
          <button className="ttr-manual-toggle" onClick={() => setShowManual(v => !v)}>
            {showManual ? "▲ Hide manual fields" : "▼ Fill manually instead"}
          </button>

          {showManual && (
            <div className="ttr-manual">
              <div className="ttr-row-2">
                <input className="ttr-input" placeholder="Display name" value={authorName}
                  onChange={e => setAuthorName(e.target.value)} />
                <input className="ttr-input" placeholder="@handle" value={authorHandle}
                  onChange={e => setAuthorHandle(e.target.value.replace(/^@/, ""))} />
              </div>
              <textarea className="ttr-input ttr-textarea" placeholder="Tweet text..."
                value={tweetText} onChange={e => setTweetText(e.target.value)} />
            </div>
          )}

          {/* Fetched author info pill */}
          {!showManual && authorName && (
            <div className="ttr-author-pill">
              <span className="ttr-author-name">{authorName}</span>
              <span className="ttr-author-handle">@{authorHandle}</span>
              {mediaList.length > 0 && (
                <span className="ttr-media-badge">
                  {mediaList[0].type === "video" ? "video" : "photo"}
                </span>
              )}
              {/* Verified toggle — auto-detected but user can override */}
              <label className="ttr-verified-toggle" title="Toggle verified badge">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={e => setVerified(e.target.checked)}
                />
                <span>✓ verified</span>
              </label>
              <button className="ttr-edit-link" onClick={() => setShowManual(true)}>edit</button>
            </div>
          )}

          {/* Generate / download */}
          {!downloadUrl ? (
            <>
              {(() => {
                // Unified progress 0–1 for both server and client paths
                const displayProgress = serverJobId
                  ? serverProgress  // real value from worker
                  : progress;       // real value from encoder
                const pct = Math.round(displayProgress * 100);

                const btnLabel = serverJobId
                  ? serverStatus === "queued"
                    ? "Queued on server…"
                    : `Converting… ${pct}%`
                  : `Encoding… ${pct}%`;

                return (
                  <>
                    <button
                      className={`ttr-generate-btn${generating ? " ttr-generate-btn--loading" : ""}`}
                      onClick={handleGenerate}
                      disabled={generating || !tweetText.trim()}
                    >
                      {generating ? (
                        <span className="ttr-btn-inner">
                          <span className="ttr-spinner" />
                          <span className="ttr-btn-label">{btnLabel}</span>
                        </span>
                      ) : "✦ Create Reel — 1 credit"}
                    </button>

                    {generating && (
                      <div className="ttr-progress">
                        <div
                          className="ttr-progress-bar"
                          style={{ width: `${pct}%`, transition: "width 1s linear" }}
                        />
                      </div>
                    )}

                    {generating && serverJobId && (
                      <p className="ttr-encoding-note">
                        <FiZap /> Rendering on server — safe to switch tabs
                      </p>
                    )}
                    {generating && !serverJobId && mediaList.some(m => m.type === "photo") && (
                      <p className="ttr-encoding-note">Keep this tab open — encoding in real time.</p>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <div className="ttr-done-row">
              <button
                className="ttr-download-btn"
                onClick={async () => {
                  try {
                    if (completedJobId) {
                      // Server-side: fetch via axios (sends JWT), then blob-download
                      const resp = await axiosInstance.get(
                        `/tweet-to-reel/download/${completedJobId}`,
                        { responseType: "blob" }
                      );
                      const url = URL.createObjectURL(resp.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "reel.mp4";
                      a.click();
                      URL.revokeObjectURL(url);
                    } else {
                      // Client-side blob URL — direct anchor download
                      const a = document.createElement("a");
                      a.href = downloadUrl;
                      a.download = `reel.${downloadExt}`;
                      a.click();
                    }
                  } catch {
                    toast.error("Download failed — try again");
                  }
                }}
              >
                ↓ Download Reel (.{downloadExt})
              </button>
              <button className="ttr-new-btn" onClick={resetForm}>New reel</button>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="ttr-preview-wrap">
          <p className="ttr-preview-label">Preview</p>
          <canvas ref={canvasRef} className="ttr-canvas" width={1080} height={1920} />
        </div>
      </div>
    </div>
  );
}
